<template>
  <div class="card-header">
    <div class="header-top">
      <div class="resource_icon">
        <i class="bi bi-file-earmark-arrow-down-fill" />
      </div>
      <div class="resource_format">
        {{ resource.getDataFormat().codename }}
      </div>
      <div class="resource_name">
        {{ resource.filedownload.href }}
      </div>
      <i
        v-if="!isURLCopied"
        class="bi bi-clipboard"
        @click="copyToClipboard"
      />
      <i
        v-else
        class="bi bi-clipboard-check"
        @click="copyToClipboard"
      />
    </div>
    <div>
      <div class="form-row">
        <div class="form-group col-6">
          <label 
            class="required"
            style="margin-top: 1em;"
          >
            {{ $t('resource.format.label') }}
          </label>
          <p class="label-help">
            {{ $t('resource.format.help') }}
          </p>
          <ValidatedFileFormatSelector
            validation-name="Format FileDownload"
            :init-value="resource.getDataFormat()"
            :format-filter="resource.getDataFormat()"
            @select="$event => setFormat($event)"
          />
          <p style="margin-top: 0.5em; font-size: 0.85em; white-space: wrap;">
            {{ $t('resource.fileUpload.formatHelp') }}
          </p>
        </div>
      </div>

      <div
        v-if="resource.isGeospatial()"
        class="form-row"
      >
        <div class="form-group col-12">
          <ResourceGeographicalLayer
            :key="`resource-geographical-layer-form-${resource.id}`"
            :resource="resource"
            :form-config="formConfig"
            :codename-disabled="true"
          />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-12">
          <label>
            {{ $t('resource.url.label') }}:
          </label>
          <input
            v-model="resource.filedownload.href"
            v-sanitize
            class="form-control"
            :placeholder="resource && resource.filedownload.href ? resource.filedownload.href : 'https://'"
          >
        </div>
      </div>
      <div class="resource-form-footer">
        <slot name="publication" />
        <b-button
          variant="outline-secondary"
          :disabled="!canUpdateResource"
          @click="modifyFileDownload"
        >
          {{ $t('buttons.update') }}
        </b-button>
      </div>
      <div
        v-if="isMain"
        class="form-row"
      >
        <label>
          {{ $t('resource.scheduler.label') }}
        </label>
        <p>
          {{ $t('resource.scheduler.help') }}
        </p>
        <b-overlay
          :show="loadingScheduler"
          rounded="lg"
          opacity="0.6"
          style="width: calc(100% - 0.5rem);"
        >
          <template #overlay>
            <div class="d-flex align-items-center">
              <b-spinner
                small
                type="grow"
                variant="secondary"
              />
              <b-spinner
                type="grow"
                variant="dark"
              />
              <b-spinner
                small
                type="grow"
                variant="secondary"
              />
              <span class="sr-only">Please wait...</span>
            </div>
          </template>
          <Multiselect
            v-model="scheduler"
            :options="schedulers"
            track-by="id"
            label="label"
            select-label=""
            selected-label=""
            deselect-label=""
            :searchable="false"
            :placeholder="$t('resource.scheduler.placeholder')"
            @select="setScheduler($event)"
            @remove="removeScheduler"
          />
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { resourceAPI } from '@/api';
import { fileDownloadAPI } from '@/api';
import { slugify } from '@/utils';
import { ErrorService } from '@/services/error-service.js';

import Multiselect from 'vue-multiselect';
import ValidatedFileFormatSelector from '@/common/components/ValidatedFileFormatSelector.vue';
import {
  ResourceGeographicalLayer
} from '@/app/resource/components';
import {  extend, configure } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
extend('required', {
  ...required,
  message: 'Ce champ est requis'
});
configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid'
  }
});

export default {
  name: 'FileDownloadEditor',

  components: {
    Multiselect,
    ResourceGeographicalLayer,
    ValidatedFileFormatSelector
  },

  props: {
    isMain: {
      type: Boolean,
      default: true
    },
    resource: {
      type: Object,
      default: () => {}
    }
  
  },

  data () {
    return {
      // scheduler
      scheduler: null,
     
      loadingScheduler: false,
      originalScheduler: null, // to know if scheduler need to be created or updated
      isURLCopied: false,
  
    };
  },

  computed: {
    ...mapState('resources', [
      'resourceDataFormats',
      'schedulers',
    ]),
    ...mapState('datasets', [
      'currentDataset'
    ]),

    canUpdateResource() {
      return this.resource.filedownload.dataformat && this.resource.filedownload.href;
    },

    formConfig() {
      return this.$config.forms.resource['file-download'];
    },
  },

  watch: {
  },

  async created() {
    if (this.schedulers.length === 0) {
      await this.GET_SCHEDULERS();
    }
    if (this.resource.scenario_execution_frequency) {
      await this.getScheduler();
    }
  },

  methods: {
    ...mapActions('resources', [
      'GET_SCHEDULERS',
    ]),
    async getScheduler(){
      const response = await resourceAPI.getNewResourceExecutionFrequency(this.resource.type,this.resource.filedownload.id);
      if (response && response.results && response.results.length) {
        this.scheduler = response.results[0].scheduler;
        this.originalScheduler = response.results[0];
      }
    },
    selectFormat(format) {
      //this.form.format = format;
      this.$set(this.resource.filedownload, 'dataformat', format.codename);
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.resource.filedownload.href);
      this.isURLCopied = true;
      setTimeout(() => {
        this.isURLCopied = false;
      }, 1000);
    },

    async setScheduler(e) {
      try {
        this.scheduler=e;
        const schedulerData = {
          scheduler: {
            class_id: this.scheduler.class_id,
            id: this.scheduler.id
          },
          kwargs: {
            dataset_id: this.currentDataset.id,
            force_update_resource_last_update_date: true,
            ...this.resource.extras && this.resource.extras.length && {
              column_x: this.resource.extras.find(el => el.key === 'column_x') ?
                this.resource.extras.find(el => el.key === 'column_x').value :
                null,
              column_y: this.resource.extras.find(el => el.key === 'column_y') ?
                this.resource.extras.find(el => el.key === 'column_y').value :
                null,
              crs: this.resource.extras.find(el => el.key === 'crs') ?
                this.resource.extras.find(el => el.key === 'crs').value :
                null,
            }
          }
        };
        if (this.scheduler) {
          this.loadingScheduler = true;
          if (!this.originalScheduler) {
            const schedulerName = `Mise à jour ${this.scheduler.id} - file_download_${this.resource.id}`;
            const scheduled = await fileDownloadAPI.setFileDownloadScheduler(
              this.resource.filedownload.id,
              {
                codename: slugify(schedulerName),
                display_name: schedulerName,
                resource_id: this.resource.id,
                ...schedulerData
              }
            );
            if (scheduled) {
              await this.getScheduler();
              ErrorService.onSuccess(true, this.$t('resource.scheduler.setMessage'));
            }
          } else {
            const scheduled = await fileDownloadAPI.updateFileDownloadScheduler(
              this.resource.filedownload.id,
              { ...schedulerData },
              this.originalScheduler.id,
            );
            if (scheduled) {
              ErrorService.onSuccess(true, this.$t('resource.scheduler.setMessage'));
            }
          }
          this.loadingScheduler = false;
          this.$emit('get');
        }
      } catch (err) {
        console.error(err);
        this.loadingScheduler = false;
      }
    },

    async removeScheduler() {
      try {
        this.loadingScheduler = true;
        const scheduled = await fileDownloadAPI.removeFileDownloadScheduler(this.resource.filedownload.id, this.originalScheduler.id);
        if (scheduled) {
          this.originalScheduler=null;
          ErrorService.onSuccess(true, this.$t('resource.scheduler.removeMessage'));
        }
        this.loadingScheduler = false;
        this.$emit('get');
      } catch (err) {
        console.error(err);
        this.loadingScheduler = false;
      }
    },

    modifyFileDownload() {
      this.$emit('modify', {
        resource:this.resource,
      });
    },
  }
};
</script>

<style lang="less" scoped>
.resource-form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 1em 1em;
  button.btn-outline-secondary {
    background-color: #F7F8FA;
    border: 2px solid #A9B2B9;
    border-radius: 8px;
    color: #2F3234;
  }
  button.btn-outline-secondary:not(.disabled) {
    color: white;
    background-color: #4b4b4b;
  }
}
i.bi-clipboard, i.bi-clipboard-check {
  color: #4b4b4b;
  font-weight: bold;
  font-size: 1.3rem;
  cursor: pointer;
  margin-left: 0.5rem;
}
</style>